import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';

// API Service
import { PlatformSettingsApiService } from '../../services/platform-settings-api.service';
import type { PlatformSettingsResponseAdminDto } from '../../api/models';

@Component({
  selector: 'app-community',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    CardModule,
    SkeletonModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.css'],
})
export class CommunityComponent implements OnInit, OnDestroy {
  // Injected services
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly apiService = inject(PlatformSettingsApiService);

  // Loading states
  loading = signal(true);
  submitting = signal(false);
  errorMessage = signal('');

  // Current settings ID
  settingsId?: string;

  // Form controls
  twitterControl = new FormControl('', [this.urlValidator]);
  facebookControl = new FormControl('', [this.urlValidator]);
  instagramControl = new FormControl('', [this.urlValidator]);
  discordControl = new FormControl('', [this.urlValidator]);
  telegramControl = new FormControl('', [this.urlValidator]);
  linkedinControl = new FormControl('', [this.urlValidator]);

  communityForm: FormGroup = new FormGroup({
    twitter: this.twitterControl,
    facebook: this.facebookControl,
    instagram: this.instagramControl,
    discord: this.discordControl,
    telegram: this.telegramControl,
    linkedin: this.linkedinControl,
  });

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Load platform settings from API
   */
  async loadSettings(): Promise<void> {
    this.loading.set(true);

    try {
      const settings: PlatformSettingsResponseAdminDto = await this.apiService.getSettings();
      this.settingsId = settings.id;

      // Populate form with existing data
      this.twitterControl.setValue(settings.twitterUrl || '');
      this.facebookControl.setValue(settings.facebookUrl || '');
      this.instagramControl.setValue(settings.instagramUrl || '');
      this.discordControl.setValue(settings.discordUrl || '');
      this.telegramControl.setValue(settings.telegramUrl || '');
      this.linkedinControl.setValue(settings.linkedinUrl || '');
    } catch (error) {
      console.error('Failed to load platform settings:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('community.errors.loadFailed'),
        life: 4000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  async onSubmit(): Promise<void> {
    if (!this.communityForm.valid || this.submitting()) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.communityForm.controls).forEach((key) => {
        this.communityForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set('');

    try {
      const data = {
        twitterUrl: this.twitterControl.value || undefined,
        facebookUrl: this.facebookControl.value || undefined,
        instagramUrl: this.instagramControl.value || undefined,
        discordUrl: this.discordControl.value || undefined,
        telegramUrl: this.telegramControl.value || undefined,
        linkedinUrl: this.linkedinControl.value || undefined,
      };

      await this.apiService.updateSocialLinks(data);

      // Show success toast
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('community.success.title'),
        detail: this.translateService.instant('community.success.message'),
        life: 4000,
      });
    } catch (error: any) {
      console.error('Failed to update social media links:', error);
      this.errorMessage.set(
        error?.error?.message || this.translateService.instant('community.errors.saveFailed')
      );

      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.errorMessage(),
        life: 4000,
      });
    } finally {
      this.submitting.set(false);
    }
  }

  // URL validator
  urlValidator(control: any) {
    if (!control.value) return null;
    const urlPattern = /^https?:\/\/.+\..+/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  // Error message methods
  getTwitterErrorMessage(): string {
    if (this.twitterControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.twitterInvalid');
    }
    return '';
  }

  getFacebookErrorMessage(): string {
    if (this.facebookControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.facebookInvalid');
    }
    return '';
  }

  getInstagramErrorMessage(): string {
    if (this.instagramControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.instagramInvalid');
    }
    return '';
  }

  getDiscordErrorMessage(): string {
    if (this.discordControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.discordInvalid');
    }
    return '';
  }

  getTelegramErrorMessage(): string {
    if (this.telegramControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.telegramInvalid');
    }
    return '';
  }

  getLinkedinErrorMessage(): string {
    if (this.linkedinControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.linkedinInvalid');
    }
    return '';
  }
}
